import React, { useCallback } from 'react';
import { Shape } from 'react-konva';
import Konva from 'konva';
import { useAppSelector } from '../../hooks/redux';
import { GRID_COLOR, GRID_COLOR_DARK } from '../../constants';

interface GridProps {
  stageSize: { width: number; height: number };
  stagePos: { x: number; y: number };
  zoomLevel: number;
}

/**
 * Single-shape grid — avoids thousands of Konva <Line> nodes that were rebuilt
 * on every pan/zoom and dominated React reconcile cost.
 */
const Grid: React.FC<GridProps> = ({ stageSize, stagePos, zoomLevel }) => {
  const gridSettings = useAppSelector((state) => state.ui.gridSettings);
  const darkMode = useAppSelector((state) => state.ui.darkMode);

  const sceneFunc = useCallback(
    (context: Konva.Context, _shape: Konva.Shape) => {
      const { size, opacity } = gridSettings;
      if (!size || size <= 0) return;

      const safeZoom = Math.max(1e-9, zoomLevel);
      const padding = 200;
      const left = -stagePos.x / safeZoom - padding;
      const top = -stagePos.y / safeZoom - padding;
      const right = left + stageSize.width / safeZoom + padding * 2;
      const bottom = top + stageSize.height / safeZoom + padding * 2;

      // Cap density: when zoomed out, coarsen the lattice so we don't stroke
      // tens of thousands of lines (visually indistinguishable at that scale).
      let step = size;
      const worldW = right - left;
      const worldH = bottom - top;
      const maxLines = 220;
      while ((worldW / step + worldH / step) > maxLines && step < size * 64) {
        step *= 2;
      }

      const startX = Math.floor(left / step) * step;
      const startY = Math.floor(top / step) * step;
      const ctx = context._context;
      ctx.beginPath();
      for (let x = startX; x <= right; x += step) {
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
      }
      for (let y = startY; y <= bottom; y += step) {
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
      }
      ctx.strokeStyle = darkMode ? GRID_COLOR_DARK : GRID_COLOR;
      ctx.globalAlpha = opacity;
      ctx.lineWidth = 1 / safeZoom;
      ctx.stroke();
      ctx.globalAlpha = 1;
    },
    [gridSettings, darkMode, stageSize, stagePos, zoomLevel]
  );

  if (!gridSettings.enabled) return null;

  return (
    <Shape
      sceneFunc={sceneFunc}
      listening={false}
      perfectDrawEnabled={false}
      hitStrokeWidth={0}
    />
  );
};

export default React.memo(Grid);
